import 'src/utils/remote-webworker'
import React, { useCallback, useState } from 'react'
import { useRecoilValue, useRecoilValueLoadable } from 'recoil'
import styled from '@emotion/styled'
import { Download } from '@emotion-icons/material/Download'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { Colors, Shapes } from 'src/components/theme'
import { Spinner } from 'src/components/spinner'
import {
  RenderPDFMsgStart,
  isRenderPDFCompleteMessage,
  isRenderPDFErrorMessage,
  isRenderPDFMsgProgress,
} from 'src/types/report-insights'
import { growlError } from 'src/components/growl'
import { browserLogger } from 'src/utils/browser-logger'
import { logEvent } from 'src/components/analytics'
import { useDownloadLimit } from 'src/views/insight-download/use-limit-downloads'
import { Badge, Typography } from '@mui/material'
import { DarkTooltip } from 'src/components/tooltip/dark-tooltip'

import * as reportInsight from 'src/atoms/report-insight'
import * as userAtoms from 'src/atoms/user'

const ButtonWrapper = styled.div`
  display: flex;
  padding-inline: ${Shapes.spacing[4]}px;
  & > span {
    width: 100%;
    & > button {
      padding-block: ${Shapes.spacing[2]}px;
    }
  }
`

type Props = {
  disabled?: boolean
}
export const InsightDownloadPDF = (props: Props) => {
  const { disabled } = props
  const fileName = useRecoilValue(reportInsight.reportFileName)
  const renderConfig = useRecoilValueLoadable(reportInsight.renderConfig)
  const [loading, setLoading] = useState(false)
  const [buttonText, setButtonText] = useState('Download')

  // Limit downloads of trial users
  const isTrial = useRecoilValue(userAtoms.isTrial)
  const { limitReached, downloadsLeft, onDecrementDownloads } = useDownloadLimit()

  const workerRef = React.useRef<Worker>()

  React.useEffect(() => {
    workerRef.current = new Worker(new URL('./pdf-worker.ts', import.meta.url))
    workerRef.current.onmessage = (event: MessageEvent<unknown>) => {
      if (isRenderPDFErrorMessage(event.data)) {
        growlError(new Error(event.data.payload), 'PDF Generation failed')
        setButtonText('Download')
        setLoading(false)
        return
      }
      if (isRenderPDFCompleteMessage(event.data)) {
        const { payload } = event.data
        const blob = new Blob([payload], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.click()
        setLoading(false)
        setButtonText('Download')
        logEvent('insight-download-pdf')
        onDecrementDownloads()
        return
      }
      if (isRenderPDFMsgProgress(event.data)) {
        setButtonText(`Downloading ${event.data.payload}%`)
      } else {
        browserLogger.warn('Unknown message from worker', event.data)
      }
    }
    return () => {
      workerRef.current?.terminate()
    }
  }, [])

  const savePagesToPDF = useCallback(() => {
    const startMsg: RenderPDFMsgStart = {
      type: 'render-pdf-start',
      payload: renderConfig.contents,
    }
    workerRef.current?.postMessage(startMsg)
  }, [renderConfig])

  const onClick = async () => {
    if (limitReached) {
      return
    }
    setLoading(true) // used to disable report interactions in the modal
    savePagesToPDF()
  }
  return (
    <ButtonWrapper>
      <DarkTooltip
        title={
          isTrial
            ? limitReached
              ? 'You have reached the download limit'
              : `You have ${downloadsLeft} report downloads remaining`
            : null
        }>
        <Badge
          color="secondary"
          badgeContent={
            isTrial && downloadsLeft > 0 ? (
              <Typography variant="text1" color="white">
                {downloadsLeft}
              </Typography>
            ) : null
          }>
          <ButtonWithIcon fullWidth inverted disabled={loading || disabled || limitReached} onClick={onClick}>
            {loading ? (
              <Spinner
                style={{
                  color: Colors.textWhite,
                }}
                size={16}
              />
            ) : (
              <Download size={16} />
            )}
            {buttonText}
          </ButtonWithIcon>
        </Badge>
      </DarkTooltip>
    </ButtonWrapper>
  )
}
